import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Close, Delete } from "@mui/icons-material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { favoriteAnim } from "../../../types/types";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { MediaPlayer, MediaProvider, Poster } from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import { isEqual } from "lodash";
import { useBeforeUnload } from "react-use";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const VIDEO_BASE_URL = import.meta.env.VITE_VIDEO_BASE_URL;
const CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_CUSTOM_UPLOAD_BASE_URL;
const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
const ManageFavourites: React.FC = () => {
  const [favourites, setFavourites] = useState<favoriteAnim[]>([]);
  const { t } = useTranslation();
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const videoRef = useRef(null);
  const customEnable = false; // Set this based on your logic

  const [initialPlaylist, setInitialPlaylist] = useState<any>([null]);
  const [initialOrderSequence, setInitialOrderSequence] = useState<any>([null]);
  const [actionType, setActionType] = useState<"save" | "reset" | null>(null);
  const [open, setOpen] = useState(false);
  const [dialogText, setDialogText] = useState("");
  const [orderSequence, setOrderSequence] = useState<any>([null]);
  const [playlistSave, setPlaylistSave] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(false);

  // Simulate a 3-second loading period
  useEffect(() => {
    if (loading === true) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useBeforeUnload(
    hasUnsavedChanges,
    "You have unsaved changes. Are you sure you want to leave?"
  );

  useEffect(() => {
    const FavListData = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "animation-favorites/list-favorites"
        );
        if (response.status >= 200 && response.status <= 300) {
          let fetchedPlaylist = response.data.data.data;
          fetchedPlaylist = fetchedPlaylist.sort(
            (a: { sequenceNo: number }, b: { sequenceNo: number }) =>
              Number(a.sequenceNo) - Number(b.sequenceNo)
          );
          setFavourites(fetchedPlaylist);
          setInitialPlaylist(fetchedPlaylist);
        }
      } catch (error) {
        console.error("Error fetching favorites");
      }
    };
    FavListData();
  }, []);

  const FavAnimListData = async () => {
    try {
      const response = await axiosInstance.get(
        API_BASE_URL + "animation-favorites/list-favorites"
      );
      if (response.status >= 200 && response.status <= 300) {
        let fetchedPlaylist = response.data.data.data;
        fetchedPlaylist = fetchedPlaylist.sort(
          (a: { sequenceNo: number }, b: { sequenceNo: number }) =>
            Number(a.sequenceNo) - Number(b.sequenceNo)
        );
        setFavourites([...fetchedPlaylist]);
        setInitialPlaylist([...fetchedPlaylist]); // Store initial value for comparison
        const initialOrder = fetchedPlaylist.map(
          (item: { id: number }) => item.id
        );
        setOrderSequence(initialOrder);
        setInitialOrderSequence(initialOrder);
        return fetchedPlaylist; // Ensure it returns the fetched data
      }
    } catch (error) {
      console.error("Error refreshing favorites");
    }
    return [];
  };

  useEffect(() => {
    if (playlistSave) {
      FavAnimListData();
      setPlaylistSave(false);
    }
  }, [playlistSave]);

  const isDisabled = useMemo(() => {

    if (!initialPlaylist.length) {
      return true; // If the initial playlist is empty, keep the button disabled
    }

    const playlistEqual = isEqual(favourites, initialPlaylist);
    const orderEqual = isEqual(orderSequence, initialOrderSequence);


    return playlistEqual && orderEqual;
  }, [favourites, initialPlaylist, orderSequence, initialOrderSequence]);



  const handleDragEnd = (event: any) => {
    const { active, over } = event;


    if (active && over && active.id !== over.id) {
      setFavourites((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);

        const updatedPlaylist = arrayMove(prev, oldIndex, newIndex);
        setOrderSequence(updatedPlaylist.map((item) => item.id));
        setHasUnsavedChanges(true); // Set unsaved changes flag
        return updatedPlaylist;
      });
    }
  };


  const handleOpen = (type: "save" | "reset") => {
    setActionType(type); // Store action type

    if (type === "save") {
      setDialogText("Would you like to save this playlist now?");
    } else if (type === "reset") {
      setDialogText(
        "Are you sure you want to reset this playlist? This action cannot be undone."
      );
    }

    setOpen(true); // Open dialog
  };

  const handleSave = async () => {
    const payload = { favoriteId: orderSequence.toString() };

    try {
      const response = await axiosInstance.put(
        API_BASE_URL + "animation-favorites/update-favorite",
        payload
      );
      if (response.status >= 200 && response.status <= 300) {
        setLoading(true);
        const updatedPlaylist = await FavAnimListData();
        setInitialOrderSequence([...orderSequence]);
        setInitialPlaylist([...updatedPlaylist]);
        setHasUnsavedChanges(false); // Clear unsaved changes flag
        toast.success("Favorites Order Changed", { autoClose: 1000 });
        setOpen(false);
        setPlaylistSave(true);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleReset = () => {
    setFavourites([...initialPlaylist]); // Restore playlist
    setOrderSequence([...initialOrderSequence]); // Restore order sequence
    setHasUnsavedChanges(false); // Clear unsaved changes flag
  };

  const handleRemove = async (id: number) => {
    const payload = { animationId: id };
    try {
      const response = await axiosInstance.delete(
        API_BASE_URL + "animation-favorites/remove-favorite",
        { data: payload }
      );
      if (response.status >= 200 && response.status <= 300) {
        setLoading(true);
        FavAnimListData();
        toast.success(response.data.message, { autoClose: 1000 });
      }
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      toast.error(err.response?.data?.message || "Something went wrong", {
        autoClose: 1000,
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Add confirmation dialog for navigation
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, _setPendingNavigation] = useState<string | null>(
    null
  );

  // Add useEffect to track changes
  useEffect(() => {
    if (!initialPlaylist.length) return;

    const playlistEqual = isEqual(favourites, initialPlaylist);
    const orderEqual = isEqual(orderSequence, initialOrderSequence);

    setHasUnsavedChanges(!playlistEqual || !orderEqual);
  }, [favourites, initialPlaylist, orderSequence, initialOrderSequence]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transform: "scale(1.4)",
            zIndex: 9999,
            backgroundImage: `url(${VISUALIZE__URL}+"link.mp4")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "50px 50px", // Set image size to 50px width and height
            backgroundColor: "#fff",
            opacity: 0.5,
          }}
        ></Box>
      ) : (
        <>
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Card
              elevation={0}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Header with centered title and action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #eee",
                    pb: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      flex: 1,
                      textAlign: "left",
                    }}
                  >
                    {t("manage_favorites")}
                  </Typography>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleOpen("reset")}
                      disabled={isDisabled}
                      sx={{
                        borderRadius: "8px",
                        px: 3,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {t("Reset")}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => handleOpen("save")}
                      disabled={isDisabled}
                      sx={{
                        borderRadius: "8px",
                        px: 3,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                        "&:not(:disabled)": {
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      {t("save")}
                    </Button>
                  </Box>
                </Box>

                {/* Content Container */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                  }}
                >
                  {/* List Section */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: { xs: "100%", md: "300px" },
                      maxWidth: { md: "400px" },
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {t("favorites_list")}
                    </Typography>

                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={favourites.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Box
                          sx={{
                            maxHeight: "600px",
                            overflowY: "auto",
                            pr: 1,
                            "&::-webkit-scrollbar": {
                              width: "6px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "rgba(0,0,0,0.2)",
                              borderRadius: "3px",
                            },
                            pl: 1,
                            pt: 1,
                            pb: 1,
                          }}
                        >
                          {favourites.map((item) => (
                            <SortableItem
                              key={item.animationId}
                              item={item}
                              handleRemove={handleRemove}
                              onSelect={() => setSelectedVideo(item.videoUrl)}
                            />
                          ))}
                        </Box>
                      </SortableContext>
                    </DndContext>
                  </Box>

                  {/* Video Preview Section */}
                  <Box
                    sx={{
                      flex: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {t("preview_favorites")}
                    </Typography>

                    {/* Video Container with 16:9 ratio */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        paddingTop: "56.25%", // This creates 16:9 ratio (9/16 = 0.5625 = 56.25%)
                        backgroundColor: "#f1f3f5",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      {selectedVideo ? (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <MediaPlayer
                            key={selectedVideo}
                            ref={videoRef}
                            id={`videoslider${selectedVideo}`}
                            data-vds-id={VIDEO_BASE_URL}
                            className="carouselvideo"
                            src={
                              customEnable
                                ? CUSTOM_UPLOAD_BASE_URL + selectedVideo
                                : VIDEO_BASE_URL + selectedVideo
                            }
                            viewType="video"
                            streamType="on-demand"
                            logLevel="warn"
                            crossOrigin
                            autoPlay
                            playsInline
                            controls
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain", // Changed to contain to prevent stretching
                              borderRadius: "8px",
                            }}
                          >
                            <MediaProvider>
                              <Poster className="vds-poster" />
                            </MediaProvider>
                            <DefaultVideoLayout icons={defaultLayoutIcons} />
                          </MediaPlayer>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f1f3f5",
                            color: "text.secondary",
                          }}
                        >
                          <Typography variant="h6">
                            {t("select_media_to_play")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
              <DialogTitle className="dialogtitle">
                Confirm Action
                <IconButton
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: "16px",
                    top: "16px",
                    border: "1px solid #be1e2d", // Red border
                    color: "#be1e2d", // Default text color
                    "&:hover": {
                      backgroundColor: "#be1e2d", // Full red background on hover
                      color: "#ffffff", // White icon color
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mt: 3, color: "black" }}>
                  {/* Are you sure you want to save the playlist? */}
                  {dialogText}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} className="cancelbtn">
                  {t("no")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="addbtn"
                  sx={{ textTransform: "none" }}
                  // onClick={handleSave}
                  onClick={() => {
                    if (actionType === "save") {
                      handleSave(); // Save changes
                    } else if (actionType === "reset") {
                      handleReset(); // Reset playlist
                    }
                    setOpen(false); // Close dialog
                  }}
                >
                  {t("yes")}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add Navigation Confirmation Dialog */}
            <Dialog
              open={showNavigationDialog}
              onClose={() => setShowNavigationDialog(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle className="dialogtitle">
                Unsaved Changes
                <IconButton
                  onClick={() => setShowNavigationDialog(false)}
                  sx={{
                    position: "absolute",
                    right: "16px",
                    top: "16px",
                    border: "1px solid #be1e2d",
                    color: "#be1e2d",
                    "&:hover": {
                      backgroundColor: "#be1e2d",
                      color: "#ffffff",
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mt: 3, color: "black" }}>
                  You have unsaved changes. Are you sure you want to leave this
                  page?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setShowNavigationDialog(false)}
                  className="cancelbtn"
                >
                  {t("stay")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="addbtn"
                  onClick={() => {
                    setShowNavigationDialog(false);
                    if (pendingNavigation) {
                    }
                  }}
                >
                  {t("leave")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </>
      )}
    </>
  );
};

interface SortableItemProps {
  item: {
    animationId: number;
    id: number;
    sequenceNo: string;
    tagName: string;
    updatedOn: string;
    userId: number;
    webSiteId: number;
    videoUrl: string;
  };
  handleRemove: (id: number) => void;
  onSelect: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  handleRemove,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        p: 1.5,
        mb: 1,
        backgroundColor: "#fff",
        borderRadius: "8px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        "&:hover": {
          backgroundColor: "#f8f9fa",
          transform: "translateX(4px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
        transitionProperty: "background-color, transform, box-shadow",
        transitionDuration: "0.2s",
        transitionTimingFunction: "ease",
      }}
      onClick={onSelect}
      role="button"
      aria-label={`Drag ${item.tagName} to reorder`}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            display: "flex",
            alignItems: "center",
          }}
          role="button"
          aria-label="Drag handle"
          tabIndex={0}
        >
          <DragIndicatorIcon
            sx={{
              color: "primary.main",
              "&:hover": { color: "primary.dark" },
            }}
          />
        </Box>

        <VideoCameraBackIcon
          sx={{ color: "primary.main" }}
          aria-hidden="true"
        />

        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {t(item.tagName)}
        </Typography>
      </Box>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleRemove(item.animationId);
        }}
        sx={{
          color: "error.main",
          "&:hover": {
            backgroundColor: "error.light",
            color: "error.dark",
          },
        }}
        aria-label={`Remove ${item.tagName} from favorites`}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default ManageFavourites;
